import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  Subscription,
} from 'rxjs';
import { IcdCodeService } from '../../shared/components/icd-code/services/icd-code.service';

@Component({
  selector: 'app-icd-code-dropdown',
  templateUrl: './icd-code-dropdown.component.html',
  styleUrl: './icd-code-dropdown.component.scss',
})
export class IcdCodeDropdownComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('resultsContainer') resultsContainer!: ElementRef;

  @Output() changeOn = new EventEmitter<any>();
  //@Input() icdCodes: any[] = [];

  icdCodes: any[] = [];
  searchQuery = '';
  showResults = false;
  totalResults = 0;
  filteredResults: any[] = [];

  loading = false;
  private loaded = false;
  private loadingStarted = false;

  private searchInputSub: Subscription | null = null;
  private documentClickSub: Subscription | null = null;

  constructor(private readonly icdCodeService: IcdCodeService) {}

  ngOnInit() {
    this.teardownSearchEvents();
  }

  ngOnDestroy(): void {
    this.searchInputSub?.unsubscribe();
    this.documentClickSub?.unsubscribe();
  }

  private loadIcdCodes(): void {
    if (this.loaded || this.loadingStarted) return;

    this.loadingStarted = true;
    this.loading = true;
    this.showResults = true;

    this.icdCodeService.getList().subscribe({
      next: (res: any[]) => {
        this.icdCodes = (res ?? []).map((icd: any) => ({
          value: icd,
          label: `${icd.code} - ${icd.name}`,
        }));

        this.totalResults = this.icdCodes.length;
        this.filteredResults = this.icdCodes.slice(0, 100);

        this.loaded = true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loaded = true;
        this.filteredResults = [];
        this.totalResults = 0;
      },
    });
  }

  cleanUnicode(str: string): string {
    return str.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');
  }

  setupSearchEvents() {
    if (this.searchInputSub) return;
    if (!this.searchInput?.nativeElement) return;

    this.searchInputSub = fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        debounceTime(300),
        map(() => this.searchQuery.toLowerCase()),
        distinctUntilChanged(),
        map((query) => {
          if (!this.loaded) {
            return [];
          }

          if (!query) {
            return this.icdCodes;
          }

          const lowerQuery = this.cleanUnicode(query).toLowerCase();

          return this.icdCodes.filter((item) => {
            const lowerLabel = this.cleanUnicode(item.label).toLowerCase();
            return lowerLabel.includes(lowerQuery);
          });
        })
      )
      .subscribe({
        next: (results) => {
          this.totalResults = results.length;
          this.filteredResults = results.slice(0, 100);
          this.showResults = true;
        },
        error: () => {
          this.showResults = true;
        },
      });

    this.documentClickSub = fromEvent(document, 'click').subscribe((event) => {
      const target = event.target as HTMLElement;

      if (target.closest('.clear-button')) return;

      const clickedInput = this.searchInput?.nativeElement?.contains(target);
      const clickedResults =
        this.resultsContainer?.nativeElement?.contains(target);

      if (!clickedInput && !clickedResults) {
        this.showResults = false;
      }
    });
  }

  teardownSearchEvents() {
    this.searchInputSub?.unsubscribe();
    this.searchInputSub = null;

    setTimeout(() => this.setupSearchEvents(), 0);
  }

  openDropdown(): void {
    this.showResults = true;
    this.loadIcdCodes();
  }

  selectItem(item: any) {
    this.searchQuery = item.label;
    this.showResults = false;
    this.changeOn.emit(item);
  }

  clearSearch(event: Event) {
    event.stopPropagation();
    this.searchQuery = '';
    this.showResults = false;
    this.changeOn.emit(null);
    this.searchInput.nativeElement.focus();
  }

  setQuery(name?: string | undefined) {
    this.searchQuery = name ?? '';
  }

  focusInputAndOpenDropdown() {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
      this.openDropdown();
    }
  }
}